<template>
	<div>
		<div class="flex flex-wrap items-center gap-2">
			<div>
				<MultiSelect
					:values="lessonPlanCollectionsOptions"
					:selectedIds="filter?.getParamValues('collections').map(val => val.id) ?? []"
					:buttonFieldClasses="'input-sm ' + fieldClasses"
					inputFieldClasses="input-sm input-bordered bg-base"
					dropdownClasses="sm:w-80 left-0"
					@itemSelected="addLessonPlanCollectionFilter"
					@itemUnselected="removeLessonPlanCollectionFilter"
				>
				{{ $t("Select lesson plan collections") }}

					<template #item="itemProps">
						<div class="flex items-start py-2 rounded hover:bg-yellow-100 px-4">
							<input
							type="checkbox"
							class="checkbox checkbox-xs rounded ml-2 mr-3 mt-0.5"
							:checked="itemProps.selected"
							/>
							<div class="overflow-hidden font-semibold line-clamp-1">
								{{ itemProps.value.label }}
							</div>
						</div>
					</template>
				</MultiSelect>
			</div>

			<div>
				<form class="flex items-center" @submit="addKeyword">
					<input type="text" class="input input-sm w-full pr-10" :placeholder="$t('Search term')":class="fieldClasses" />
					<button type="submit" class="py-1 -ml-9 pl-2 pr-1 border-l">
						<MagnifyingGlassIcon class="w-4 h-4" />
					</button>
				</form>
			</div>
		</div>

		<div class="flex gap-2 items-start">

			<div class="grow flex flex-wrap gap-x-1">
				<button v-if="!filterIsEmpty" type="button" class="btn btn-xs btn-ghost mt-2" @click="resetFilter">
					{{ $t('Reset') }}
				</button>
				<Tag
					v-for="filterVal in filter?.getParamValues('collections')"
					:key="filterVal.id"
					:removeable="true"
					class="tag-collection tag-xs mt-2"
					@remove="removeFilterValue('collections', filterVal)"
				>
					{{ filterVal.label }}
				</Tag>

				<Tag
					v-for="filterVal in filter?.getParamValues('keywords')"
					:key="filterVal.id"
					:removeable="true"
					class="tag-keyword tag-with-icon tag-xs mt-2"
					@remove="removeFilterValue('keywords', filterVal)"
				>
					{{ filterVal.label }}
				</Tag>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { mapStores } from "pinia";
import {useLessonPlanCollectionsStore} from "@/stores/LessonPlanCollections.store.ts";
import { FilterParamValue } from "@/stores/filters/FilterParamValue";
import { LessonPlanFilterDef } from "@/stores/filters/LessonPlanFilterDef";
import { PropType } from "vue";

import MultiSelect from "@/components/ui/MultiSelect.vue";
import { CheckIcon, FunnelIcon, ChevronDownIcon, XMarkIcon, MagnifyingGlassIcon } from "@heroicons/vue/24/outline";
import Tag from "@/components/ui/Tag.vue";

export default {
	// This component is responsible for displaying and managing the filter options for the lesson plan list.
	// It does not manage the actual filtering of the lesson plans itself, but rather emits events to notify
	// the parent component of changes in the filter options.
	name: "LessonPlanFilter",

	emits: ["change", "reset"],

	props: {
		filter: {
			type: Object as PropType<LessonPlanFilterDef>,
		},
		fieldClasses: {
			type: String,
			default: "",
		},
	},

	components: {
		MultiSelect,
		Tag,
		CheckIcon,
		FunnelIcon,
		ChevronDownIcon,
		XMarkIcon,
		MagnifyingGlassIcon,
	},

	computed: {
		...mapStores(useLessonPlanCollectionsStore),

		filterIsEmpty() {
			if (!this.filter) return true;
			return this.filter.isEmpty();
			// Ideally we compare with the default filter, but for now we just check if there are any values
			// in the filter at all (eliminating the need for the lessonPlansStore in this component).
			// const dfltFilter = this.lessonPlansStore.getDefaultFilter();
			// return this.filter.equals(dfltFilter);
		},

		lessonPlanCollectionsOptions() {
			return this.lessonPlanCollectionsStore.getItems().map((collection: any) => {
				return new FilterParamValue(
					collection.id,
					collection.name
				);
			});
		}
	},

	methods: {

		resetFilter() {
			// notify parent
			this.$emit("reset");
		},

		async addKeyword(event: any) {
			event.preventDefault();

			const keyword = event.target[0].value.trim();
			if(!keyword || keyword.length < 3) {
				return;
			}
			event.target[0].value = '';
			await this.addFilterValue("keywords", new FilterParamValue(keyword, keyword));
		},

		async addFilterValue(paramName: string, paramValue: FilterParamValue) {
			if (!this.filter) return;

			// update the filter instance
			this.filter.addParamValue(paramName, paramValue);

			// notify parent
			this.$emit("change", this.filter);
		},

		async removeFilterValue(paramName, paramValue: FilterParamValue) {
			if (!this.filter) return;

			this.filter.removeParamValue(paramName, paramValue);

			// notify parent
			this.$emit("change", this.filter);
		},

		addLessonPlanCollectionFilter(paramValue: FilterParamValue) {
			this.addFilterValue("collections", paramValue);
		},

		removeLessonPlanCollectionFilter(paramValue: FilterParamValue) {
			this.removeFilterValue("collections", paramValue);
		},
	},
};
</script>
